import { useEffect } from 'react';
import { DeviceCardProps } from '../../Components/CodeEditor/Devices/DeviceCardProps';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { useAppDispatch } from '../../redux/ReduxHooks';
import { isTrackedDriveUnitPort, isUnsupportedDriveUnitPortRawValue } from '../../utils/driveUnitPortSelection';
import { useControlInitialization, type UseControlInitializationOptions } from './useControlInitialization';

type PortInitOptions = Pick<
  UseControlInitializationOptions<number>,
  'min' | 'max' | 'parseValue' | 'isUnsupportedRawValue' | 'validateValue' | 'onValidationError'
>;

/**
 * Reads a drive-unit port from program code via `useControlInitialization` while the card stays
 * mounted above collapsible UI, and mirrors the value into `application.driveUnitPorts` so port
 * slot icons can grey out ports used elsewhere.
 *
 * When the port changes because the user edited the constructor in Monaco, stream sync is
 * handled by `useDriveUnitPortStreamSyncFromEditor` (same `applyDriveUnitPortStreamSync` path as the UI).
 */
export function useDriveUnitPortRegistry(
  props: DeviceCardProps,
  portParameterName: string,
  controlInitOptions: Partial<PortInitOptions> = {}
): {
  port: number | undefined;
  setPort: (value: number) => void;
  portControlValue: number | undefined;
  setPortControlValue: (value: number) => void;
  unsupportedRawValue?: string;
} {
  const dispatch = useAppDispatch();

  const portProps: DeviceCardProps = { ...props, parameterName: portParameterName };
  const {
    value: port,
    setValue: setPort,
    unsupportedRawValue,
  } = useControlInitialization<number>(portProps, {
    parseValue: (v) => Number.parseInt(v, 10),
    isUnsupportedRawValue: isUnsupportedDriveUnitPortRawValue,
    ...controlInitOptions,
  });
  const electronicKey = `${props.id}:${portParameterName}`;
  const registryRev = props.driveUnitPortRegistryRev ?? 0;
  const effectivePort = unsupportedRawValue ? undefined : port;

  useEffect(() => {
    if (!isTrackedDriveUnitPort(effectivePort)) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: electronicKey }));
      return;
    }
    dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: effectivePort, electronic: electronicKey }));
  }, [dispatch, effectivePort, electronicKey, registryRev]);

  return { port, setPort, portControlValue: effectivePort, setPortControlValue: setPort, unsupportedRawValue };
}
